if (!customElements.get("testimonials-carousel")) {
  const FULLSCREEN_EVENTS = ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"];
  const FULLSCREEN_STYLES = {
    "object-fit": "contain",
    "background-color": "#000",
    "border-radius": "0",
    width: "100%",
    height: "100%",
  };
  const PLAYING = "testimonials-carousel__video--playing";
  const STARTED = "testimonials-carousel__video--started";

  const fullscreenElement = () =>
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null;

  const unmute = (video) => {
    video.removeAttribute("muted");
    video.muted = false;
  };

  const setControls = (video, enabled) => {
    if (enabled) video.setAttribute("controls", "");
    else video.removeAttribute("controls");
    video.controls = enabled;
  };

  const wrapperOf = (element) => element.closest("[data-testimonial-video]");

  customElements.define(
    "testimonials-carousel",
    class TestimonialsCarousel extends HTMLElement {
      connectedCallback() {
        this.onFullscreenChange ??= () => {
          const active = fullscreenElement();
          this.videos().forEach((video) => {
            for (const [property, value] of Object.entries(FULLSCREEN_STYLES)) {
              if (video === active) video.style.setProperty(property, value, "important");
              else video.style.removeProperty(property);
            }
          });
        };
        FULLSCREEN_EVENTS.forEach((type) => document.addEventListener(type, this.onFullscreenChange));

        if (this.initialized) return;
        this.initialized = true;

        this.videos().forEach((video) => {
          unmute(video);
          setControls(video, false);
          video.addEventListener(
            "loadedmetadata",
            () => {
              unmute(video);
              setControls(video, false);
            },
            { once: true },
          );
          // The poster <img> covers the video until its first frame plays.
          video.addEventListener("playing", () => wrapperOf(video)?.classList.add(STARTED), { once: true });
        });

        this.addEventListener("click", (event) => {
          const button = event.target.closest("[data-testimonial-play]");
          if (button && this.contains(button)) this.toggle(wrapperOf(button));
        });

        // Media events don't bubble, so these listen in the capture phase.
        this.addEventListener(
          "pause",
          (event) => {
            const video = event.target;
            if (!(video instanceof HTMLVideoElement)) return;
            const wrapper = wrapperOf(video);
            if (!wrapper) return;

            // Seeking pauses briefly; wait before treating it as a real pause.
            setTimeout(() => {
              if (video.seeking || !video.paused) return;
              wrapper.classList.remove(PLAYING);
              setControls(video, false);
            }, 150);
          },
          true,
        );

        this.addEventListener(
          "ended",
          (event) => {
            const video = event.target;
            if (!(video instanceof HTMLVideoElement)) return;
            const wrapper = wrapperOf(video);
            if (!wrapper) return;
            wrapper.classList.remove(PLAYING);
            setControls(video, false);
          },
          true,
        );
      }

      disconnectedCallback() {
        FULLSCREEN_EVENTS.forEach((type) => document.removeEventListener(type, this.onFullscreenChange));
      }

      videos() {
        return this.querySelectorAll("[data-testimonial-video] video");
      }

      toggle(wrapper) {
        const video = wrapper?.querySelector("video");
        if (!video) return;

        if (!video.paused && !video.ended) {
          video.pause();
          wrapper.classList.remove(PLAYING);
          setControls(video, false);
          return;
        }

        this.querySelectorAll("[data-testimonial-video]").forEach((other) => {
          if (other === wrapper) return;
          const otherVideo = other.querySelector("video");
          if (otherVideo) {
            otherVideo.pause();
            setControls(otherVideo, false);
          }
          other.classList.remove(PLAYING);
        });

        unmute(video);

        const started = () => {
          wrapper.classList.add(PLAYING);
          setControls(video, true);
        };

        video
          .play()
          .then(started)
          .catch(() => {
            // Autoplay policy refused sound; play muted instead.
            video.muted = true;
            video
              .play()
              .then(started)
              .catch(() => {});
          });
      }
    },
  );
}
